import {
  TimelineStart,
  StepIndicator,
  TimelineIndicator,
  TimelineEnd,
} from "~/molecules/WorkflowTimelineIndicators";
import { useTranslation } from "react-i18next";
import {
  WorkflowStepStatuses,
  type DocumentDetailsRelease,
  type DocumentReleaseDetails,
} from "~/api/types";
import { Link } from "react-router";
import { Button } from "~/atoms/button";
import { Archive, BookLock, CircleX } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "react-toastify";
import { moveWorkflow } from "~/api/v2/requests";
import { formatDate } from "~/helpers/date";
import { SetAdoptionDateModal } from "~/molecules/SetAdoptionDateModal";
import { PublishModal } from "~/molecules/PublishModal";
import { WorkflowOverviewStepControlContainer } from "~/molecules/WorkflowOverviewStepControlContainer";
import { WorkflowOverviewStep } from "~/molecules/WorkflowOverviewStep";
import WorkflowDescription from "~/molecules/WorkflowDescription";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/atoms/alert-dialog";
import {
  canEditWorkflow,
  canManageArtifact,
  canPublishArtifact,
  canSetWorkflowAdoptionDate,
  getWorkflowStepControlPermissions,
  isDraftArtifact,
} from "~/helpers/documentPermissions";
import NoResult from "~/molecules/NoResult";
import WorkflowDates from "~/molecules/WorkflowDates";

function CancelWorkflowDialog({
  onConfirm,
  children,
}: {
  onConfirm: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(($) => $.commons.workflow.cancelWorkflowTitle)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(($) => $.commons.workflow.cancelWorkflowDescription)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t(($) => $.commons.workflow.abort)}
          </AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={onConfirm}>
            {t(($) => $.commons.workflow.cancelWorkflow)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface Props {
  releaseDetails?: DocumentReleaseDetails;
  selectedRelease: DocumentDetailsRelease;
  onPublish: (releaseId: number) => void;
  onChange: () => void;
  /** A following copy's adoption mirrors its source's. */
  followsSource: boolean;
}

export const WorkflowOverview = ({
  releaseDetails,
  selectedRelease,
  onPublish,
  onChange,
  followsSource,
}: Props) => {
  const { t } = useTranslation();

  const isDraft = isDraftArtifact(selectedRelease);

  const [isWorkflowExpanded, setIsWorkflowExpanded] = useState(isDraft);
  const [prevIsDraft, setPrevIsDraft] = useState(isDraft);
  if (prevIsDraft !== isDraft) {
    setPrevIsDraft(isDraft);
    setIsWorkflowExpanded(isDraft);
  }

  const [isCancelling, setIsCancelling] = useState(false);

  const canEditCurrentWorkflow = canEditWorkflow(selectedRelease);
  const canManage = canManageArtifact(selectedRelease);

  if (releaseDetails === undefined) {
    return <NoResult />;
  }

  const artifact = releaseDetails.artifact;

  const handleCancelWorkflow = async () => {
    try {
      setIsCancelling(true);
      await moveWorkflow(artifact.id, "cancel");
      toast.success(t(($) => $.toasts.success.workflowCancelled));
      void onChange();
    } catch {
      toast.error(t(($) => $.toasts.error.workflowNotCancelled));
    } finally {
      setIsCancelling(false);
    }
  };

  const lastStep = artifact.steps[artifact.steps.length - 1];
  const lastStepIndex = artifact.steps.length - 1;
  const isLastStepActive = lastStep?.status === WorkflowStepStatuses.active;

  const showSetAdoptionDate = canSetWorkflowAdoptionDate(
    selectedRelease,
    isLastStepActive,
  );

  const { showPublishButton } = getWorkflowStepControlPermissions({
    canManage,
    canPublish: canPublishArtifact(selectedRelease),
    isWorkflowStarted: artifact.isOngoing,
    isActiveStep: isLastStepActive,
    isFirstStep: lastStepIndex === 0,
    isLastStep: true,
    artifactStatus: selectedRelease.artifact.status,
  });

  const hasNoActivity =
    artifact.stepInstances.length === 1 &&
    (artifact.stepInstances[0]?.comments.length ?? 0) === 0;
  const showCancelButton =
    canManage && isDraft && artifact.isOngoing && hasNoActivity;

  return (
    <div className="flex flex-col gap-4">
      {canEditCurrentWorkflow && (
        <Button variant="outline" asChild>
          <Link to={`/workflows/${releaseDetails.id}/edit`}>
            {t(($) => $.commons.workflow.editWorkflow)}
          </Link>
        </Button>
      )}
      <WorkflowDates release={releaseDetails} />
      <WorkflowDescription description={artifact.description} />
      <div className="relative grid grid-cols-[3rem_1fr] gap-x-2 gap-y-4">
        <div className="bg-primary/40 absolute top-0 bottom-0 left-6 z-0 w-0.5" />
        <TimelineStart startedAt={artifact.stepInstances[0]?.createdAt}>
          {showCancelButton && (
            <CancelWorkflowDialog onConfirm={() => void handleCancelWorkflow()}>
              <Button variant="outline" disabled={isCancelling} size="sm">
                <CircleX />
                {t(($) => $.commons.workflow.cancelWorkflow)}
              </Button>
            </CancelWorkflowDialog>
          )}
        </TimelineStart>
        {isWorkflowExpanded ? (
          artifact.steps.map((step, index) => (
            <WorkflowOverviewStepControlContainer
              key={step.id}
              step={step}
              stepIndex={index}
              totalSteps={artifact.steps.length}
              documentArtifactId={artifact.id}
              selectedRelease={selectedRelease}
              isWorkflowStarted={artifact.isOngoing}
              onChange={onChange}
            >
              <StepIndicator status={step.status} />
              <div className="min-w-0">
                <WorkflowOverviewStep
                  step={step}
                  stepInstances={artifact.stepInstances}
                  artifactStatus={selectedRelease.artifact.status}
                  previousStepName={artifact.steps[index - 1]?.name}
                  onUserResponse={onChange}
                />
              </div>
            </WorkflowOverviewStepControlContainer>
          ))
        ) : (
          <>
            <StepIndicator value="..." status={WorkflowStepStatuses.upcoming} />
            <div className="flex items-center text-sm italic">
              {artifact.steps.length}{" "}
              {t(($) => $.commons.workflow.completedSteps)}
            </div>
          </>
        )}

        {!isDraft && (
          <div className="col-start-2 flex justify-center">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setIsWorkflowExpanded(!isWorkflowExpanded)}
            >
              {isWorkflowExpanded
                ? t(($) => $.commons.workflow.hideSteps)
                : t(($) => $.commons.workflow.showSteps)}
            </Button>
          </div>
        )}

        {showSetAdoptionDate && (
          <TimelineIndicator
            icon={BookLock}
            text={`${t(($) => $.commons.document.adopted)} ${formatDate(selectedRelease.adoptedAt, "date")}`}
            variant={selectedRelease.adoptedAt ? "primary" : "secondary"}
          />
        )}
        {showSetAdoptionDate && !followsSource && (
          <div className="col-start-2 flex justify-center">
            <SetAdoptionDateModal
              adoptedAt={selectedRelease.adoptedAt}
              releaseId={selectedRelease.id}
              onChange={onChange}
            />
          </div>
        )}

        {showPublishButton && (
          <div className="col-start-2 flex justify-center">
            <PublishModal
              onConfirm={onPublish}
              canManage={canManage}
              selectedRelease={selectedRelease}
              scheduleReviewPeriod={
                releaseDetails.document.scheduleReviewPeriod
              }
              lastStep={lastStep}
            />
          </div>
        )}

        <TimelineEnd
          reviewTo={artifact.reviewTo}
          publishedAt={artifact.publishedAt}
        />

        {!isDraft && (
          <TimelineIndicator
            icon={Archive}
            text={`${t(($) => $.commons.document.statuses.archived)} ${formatDate(selectedRelease.artifact.archivedAt, "date")}`}
            variant={
              selectedRelease.artifact.archivedAt ? "primary" : "secondary"
            }
          />
        )}
      </div>
    </div>
  );
};
